import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import brandsData from '../data/brands_data.json';
import { 
  Users, 
  FileText, 
  Settings, 
  PlusCircle, 
  Search, 
  Filter,
  LogOut,
  ChevronRight,
  TrendingUp,
  ShieldCheck,
  Globe,
  Sparkles,
  RefreshCw,
  Database,
  Download
} from 'lucide-react';

const API_URL = '/api.php';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [analyzing, setAnalyzing] = useState(null); 
  const [aiResults, setAiResults] = useState({});
  const [selectedAudit, setSelectedAudit] = useState(null); 
  const [entities, setEntities] = useState([]); 
  const [selectedClient, setSelectedClient] = useState(null); // Drill-level: Client
  const [selectedRound, setSelectedRound] = useState(null);   
  const [rounds, setRounds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activeView, setActiveView] = useState('overview'); 
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeView === 'entities') {
      fetchEntities();
    }
    if (activeView === 'logs') {
      fetchLogs();
    }
  }, [activeView]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_activity`);
      setLogs(res.data);
    } catch (err) {
      console.error("Failed to fetch logs", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_entities&type=client`);
      setEntities(res.data);
    } catch (err) {
      console.error("Failed to fetch entities", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRounds = async (clientId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_entities&type=round&parent_id=${clientId}`);
      setRounds(res.data);
    } catch (err) {
      console.error("Failed to fetch rounds", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntity = async (type, title, parentId = 0) => {
    try {
      await axios.post(`${API_URL}?action=create_entity`, { type, title, parent_id: parentId });
      type === 'client' ? fetchEntities() : fetchRounds(parentId);
    } catch (err) {
      alert("Creation failed.");
    }
  };
  const [auditScores, setAuditScores] = useState({
    1: { score: 5, reasoning: '' }, // The Rhythm Rule
    2: { score: 5, reasoning: '' }, // The Phonetic Anchor
    3: { score: 5, reasoning: '' }, // Market Connectivity
    4: { score: 5, reasoning: '' }  // Visual Symmetry
  });
  const [savingAudit, setSavingAudit] = useState(false);

  const brandingLaws = [
    { id: 1, name: 'The Rhythm Rule', desc: 'Analysis of syllables (2-3 preferred) and phonetic movement.' },
    { id: 2, name: 'The Phonetic Anchor', desc: 'Presence of strong consonants (K, R, T) for technical resonance.' },
    { id: 3, name: 'The Market Connectivity', desc: 'How well the name bridges the gap between the JV partners.' },
    { id: 4, name: 'The Visual Symmetry', desc: 'Balanced letter structure for premium logo design potential.' }
  ];

  const handleSaveAudit = async () => {
    if (!selectedAudit) return;
    setSavingAudit(true);
    try {
      for (const lawId in auditScores) {
        await axios.post(`${API_URL}?action=save_audit_result`, {
          candidate_id: selectedAudit.id || 1, // Mocking ID if not present in JSON
          law_id: lawId,
          score: auditScores[lawId].score,
          reasoning: auditScores[lawId].reasoning
        });
      }
      alert('Audit strategy saved to database.');
      setSelectedAudit(null);
    } catch (err) {
      console.error(err);
      alert('Failed to save audit.');
    } finally {
      setSavingAudit(false);
    }
  };
  
  const stats = [
    { label: 'Total Brands', value: brandsData.length, icon: FileText, color: 'text-brand' },
    { label: 'Client Rounds', value: '7', icon: TrendingUp, color: 'text-blue-400' },
    { label: 'Top Tier (5★)', value: brandsData.filter(b => b.stars.length >= 5).length, icon: ShieldCheck, color: 'text-green-400' },
    { label: 'Domains Checked', value: '1,200+', icon: Globe, color: 'text-purple-400' },
  ];

  const filteredBrands = brandsData.filter(b => 
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.source.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 15); // Show first 15 for performance in demo

  return (
    <div className="flex min-h-screen bg-industrial-900 text-white font-sans">
      {/* Sidebar */}
      <aside className="w-64 border-r border-industrial-800 flex flex-col p-6">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center text-industrial-900 font-bold">D</div>
          <span className="font-bold tracking-tight uppercase text-sm">Dexter Admin</span>
        </div>

        <nav className="flex-1 space-y-2">
          <NavItem icon={LayoutDashboard} label="Overview" active={activeView === 'overview'} onClick={() => setActiveView('overview')} />
          <NavItem icon={Users} label="Client Projects" active={activeView === 'entities'} onClick={() => setActiveView('entities')} />
          <NavItem icon={FileText} label="Recent Audits" onClick={() => {}} />
          <NavItem icon={ShieldCheck} label="Branding Laws" onClick={() => window.location.href='/admin/laws'} />
          <NavItem icon={Database} label="System Backup" onClick={() => window.open(`${API_URL}?action=export_db`, '_blank')} />
          <NavItem icon={Settings} label="Toolkit Settings" onClick={() => {}} />
        </nav>

        <button 
          onClick={logout}
          className="mt-auto flex items-center gap-3 text-industrial-500 hover:text-red-400 transition-colors p-2"
        >
          <LogOut className="w-5 h-5" />
          <span>Sign Out</span>
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold">Administrative Hub</h1>
            <p className="text-industrial-400 text-sm">Welcome back, {user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-industrial-500" />
              <input 
                type="text" 
                placeholder="Search brands or rounds..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-industrial-800 border border-industrial-700 rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-brand outline-none w-64"
              />
            </div>
            <button className="btn-primary py-2 px-4 text-sm flex items-center gap-2">
              <PlusCircle className="w-4 h-4" /> New Audit
            </button>
          </div>
        </header>

        {activeView === 'overview' ? (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
              {stats.map((s, i) => (
                <div key={i} className="glass-card p-6 border-industrial-800">
                  <div className="flex items-center justify-between mb-2">
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                    <span className="text-xs text-industrial-500 font-mono">LIVE</span>
                  </div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-industrial-400 uppercase tracking-widest mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Recent Brands Table */}
            <div className="glass-card overflow-hidden border-industrial-800">
              <div className="px-6 py-4 border-b border-industrial-800 flex justify-between items-center">
                <h3 className="font-semibold">Cumulative Brand History</h3>
                <button className="text-xs text-brand hover:underline flex items-center gap-1">
                   Export as Editable PDF <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <table className="w-full text-left">
                <thead className="bg-industrial-800/50 text-industrial-400 text-xs uppercase tracking-widest">
                  <tr>
                    <th className="px-6 py-3 font-medium">Brand Name</th>
                    <th className="px-6 py-3 font-medium">Syllables</th>
                    <th className="px-6 py-3 font-medium">Priority</th>
                    <th className="px-6 py-3 font-medium">Source Round</th>
                    <th className="px-6 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-industrial-800">
                  {filteredBrands.map((brand, i) => (
                    <tr key={i} className="hover:bg-white/5 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="font-bold text-white group-hover:text-brand transition-colors">{brand.name}</div>
                        <div className="text-[10px] text-industrial-500 uppercase mt-0.5 tracking-tighter">
                          {brand.display_name !== brand.name ? brand.display_name : 'Primary Key'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 bg-industrial-700 rounded text-xs">
                          {brand.syllables} Syl
                        </span>
                      </td>
                      <td className="px-6 py-4 text-brand tracking-widest">{brand.stars}</td>
                      <td className="px-6 py-4 text-sm text-industrial-400">Round {brand.source}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={async () => {
                              setAnalyzing(brand.name);
                              try {
                                const historySummary = brandsData.slice(0, 10).map(b => `${b.name} (${b.stars} stars)`).join(', ');
                                const res = await axios.post(`${API_URL}?action=gemini_analyze`, { 
                                  brand_name: brand.name,
                                  project_context: "Dexter Communications branding for Kova × Rand York Joint Venture (Metallurgy/Structural Steel).",
                                  history_summary: historySummary
                                });
                                const aiText = res.data?.candidates?.[0]?.content?.parts?.[0]?.text || "Analysis failed.";
                                setAiResults(prev => ({ ...prev, [brand.name]: res.data }));
                                
                                // Also save to DB as a "system note" so the client can see it
                                await axios.post(`${API_URL}?action=save_feedback`, {
                                  brand_name: brand.name,
                                  client_username: 'SYSTEM_AI',
                                  comment: aiText,
                                  status: 'approved'
                                });
                              } catch (err) {
                                console.error(err);
                              } finally {
                                setAnalyzing(null);
                              }
                            }}
                            disabled={analyzing === brand.name}
                            className="text-industrial-500 hover:text-brand transition-colors p-1 rounded hover:bg-brand/10"
                            title="Run AI Analysis"
                          >
                            {analyzing === brand.name ? <RefreshCw className="w-4 h-4 animate-spin text-brand" /> : <Sparkles className="w-4 h-4" />}
                          </button>
                          <button 
                            onClick={() => setSelectedAudit(brand)}
                            className="text-industrial-500 hover:text-white transition-colors p-1"
                            title="Open Strategic Audit"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                        {aiResults[brand.name] && (
                          <div className="absolute z-50 mt-2 w-64 p-4 glass-card text-[10px] shadow-2xl animate-fade-in border-brand/20">
                             <div className="font-bold text-brand uppercase mb-1">AI Insights</div>
                             <div className="text-slate-300 leading-tight">
                                {aiResults[brand.name]?.candidates?.[0]?.content?.parts?.[0]?.text || "Analysis failed."}
                             </div>
                             <button onClick={() => setAiResults(p => { delete p[brand.name]; return {...p} })} className="mt-2 text-industrial-500 hover:text-white underline">Close</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-3 bg-industrial-800/20 text-center">
                <button className="text-industrial-500 text-xs hover:text-brand transition-colors">
                  View All 345 Brand Entries
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="glass-card p-8 border-brand/10 min-h-[600px]">
            <header className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Users className="w-6 h-6 text-brand" /> 
                  {selectedClient ? `Project: ${selectedClient.title}` : "Master Entity Manager"}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <button 
                    onClick={() => { setSelectedClient(null); setSelectedRound(null); }}
                    className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${!selectedClient ? 'bg-brand text-black' : 'bg-industrial-800 text-industrial-400'}`}
                  >
                    Clients
                  </button>
                  {selectedClient && <ChevronRight className="w-3 h-3 text-industrial-600" />}
                  {selectedClient && (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-brand text-black">
                      Rounds
                    </span>
                  )}
                </div>
              </div>
              
              {!selectedClient && (
                <button 
                  onClick={() => {
                    const title = prompt("Enter Client Name:");
                    if (title) handleCreateEntity('client', title);
                  }}
                  className="btn-primary py-2 px-4 text-xs flex items-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" /> New Project
                </button>
              )}
              {selectedClient && !selectedRound && (
                 <button 
                  onClick={() => {
                    const title = prompt("Enter Round Version (e.g. 3.3.2):");
                    if (title) handleCreateEntity('round', title, selectedClient.id);
                  }}
                  className="btn-primary py-2 px-4 text-xs flex items-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" /> New Round
                </button>
              )}
            </header>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               {loading ? (
                 <div className="col-span-3 flex items-center justify-center p-20">
                    <RefreshCw className="w-8 h-8 animate-spin text-brand" />
                 </div>
               ) : !selectedClient ? (
                 entities.map((entity) => (
                   <div key={entity.id} className="p-6 bg-brand/5 rounded-2xl border border-brand/20 hover:border-brand/50 transition-all flex flex-col justify-between">
                      <div>
                        <div className="text-xs font-mono text-brand mb-2">ENTITY: {entity.slug?.toUpperCase() || entity.id}</div>
                        <div className="text-lg font-bold">{entity.title}</div>
                        <div className="text-xs text-industrial-400 mt-1 uppercase">
                           {entity.child_count} Active Rounds | {new Date(entity.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button 
                        onClick={() => { setSelectedClient(entity); fetchRounds(entity.id); }}
                        className="mt-6 w-full py-2 bg-industrial-800 hover:bg-brand/20 border border-industrial-700 hover:border-brand/50 rounded-xl text-xs font-bold text-center transition-all"
                      >
                        Manage Project
                      </button>
                   </div>
                 ))
               ) : (
                 rounds.map((round) => (
                    <div key={round.id} className="p-6 bg-industrial-800/30 rounded-2xl border border-industrial-700 hover:border-brand/30 transition-all flex flex-col justify-between">
                      <div>
                        <div className="text-xs font-mono text-industrial-500 mb-2">ROUND VERSION: {round.title}</div>
                        <div className="text-lg font-bold">Strategic Audit</div>
                        <div className="text-xs text-industrial-400 mt-1 uppercase">
                           {round.child_count} Candidates | Status: {round.status}
                        </div>
                      </div>
                      <button 
                        onClick={() => { setSelectedRound(round); setActiveView('overview'); /* Logic to filter candidates by round */ }}
                        className="mt-6 w-full py-2 bg-brand/10 hover:bg-brand/20 border border-brand/20 rounded-xl text-xs font-bold text-brand text-center transition-all"
                      >
                        Process Candidates
                      </button>
                    </div>
                 ))
               )}
            </div>
            
            {!loading && !selectedClient && entities.length === 0 && (
               <div className="text-center py-20 text-industrial-500 italic text-sm">
                  No client projects found. Start by creating a new project.
               </div>
            )}
            {!loading && selectedClient && rounds.length === 0 && (
               <div className="text-center py-20 text-industrial-500 italic text-sm">
                  No rounds found for this project. Start by creating v1.0.
               </div>
            )}
          </div>
        )}

        {activeView === 'logs' && (
          <div className="glass-card p-8 border-industrial-800">
            <header className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-6 h-6 text-brand" /> System Activity Stream
                </h2>
                <p className="text-industrial-400 text-sm mt-1">Real-time audit log of all administrative and automated actions.</p>
              </div>
              <a 
                href={`${API_URL}?action=export_activity`} 
                download 
                className="btn-primary py-2 px-4 text-xs flex items-center gap-2"
              >
                <ChevronRight className="w-4 h-4 rotate-90" /> Download logs (.csv)
              </a>
            </header>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-industrial-800/50 text-industrial-400 text-[10px] uppercase tracking-widest border-b border-industrial-700">
                  <tr>
                    <th className="px-6 py-4 font-medium">Timestamp</th>
                    <th className="px-6 py-4 font-medium">Actor</th>
                    <th className="px-6 py-4 font-medium">Action</th>
                    <th className="px-6 py-4 font-medium">Target</th>
                    <th className="px-6 py-4 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-industrial-800">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-white/5 transition-colors group">
                      <td className="px-6 py-4 text-xs font-mono text-industrial-400">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-brand/10 text-brand text-[10px] font-bold rounded uppercase">
                          {log.user_username}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold">{log.action_type}</td>
                      <td className="px-6 py-4 text-xs text-industrial-300">{log.entity_type} {log.entity_id}</td>
                      <td className="px-6 py-4 text-xs text-industrial-500 italic max-w-xs truncate" title={log.details}>
                        {log.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && (
                <div className="flex justify-center p-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-brand" />
                </div>
              )}
              {!loading && logs.length === 0 && (
                <div className="text-center py-20 text-industrial-500 italic text-sm">
                  No activity logs recorded yet.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Audit Manager Modal */}
        {selectedAudit && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-industrial-950/80 backdrop-blur-sm animate-fade-in">
            <div className="w-full max-w-4xl glass-card max-h-[90vh] overflow-y-auto shadow-2xl border-brand/20 p-8">
              <header className="flex justify-between items-start mb-8 border-b border-industrial-800 pb-6">
                <div>
                  <div className="text-brand text-xs font-mono uppercase tracking-[0.3em] mb-1">Strategic Audit Manager</div>
                  <h2 className="text-3xl font-bold tracking-tight">{selectedAudit.name}</h2>
                  <p className="text-industrial-400 text-sm mt-1">{selectedAudit.meaning || "Analyzing brand resonance for metallurgy sector."}</p>
                </div>
                <button 
                  onClick={() => setSelectedAudit(null)}
                  className="text-industrial-500 hover:text-white transition-colors"
                >
                  <PlusCircle className="w-6 h-6 rotate-45" />
                </button>
              </header>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                {brandingLaws.map((law) => (
                  <div key={law.id} className="space-y-4 p-5 bg-industrial-800/30 rounded-xl border border-industrial-800/50 hover:border-brand/20 transition-all">
                    <div className="flex justify-between items-center">
                      <h4 className="font-bold text-sm uppercase tracking-wider">{law.name}</h4>
                      <span className="text-brand font-mono text-xl">{auditScores[law.id].score}/10</span>
                    </div>
                    <p className="text-[10px] text-industrial-500 italic leading-relaxed">{law.desc}</p>
                    <input 
                      type="range" min="0" max="10" step="1"
                      value={auditScores[law.id].score}
                      onChange={(e) => setAuditScores(prev => ({
                        ...prev,
                        [law.id]: { ...prev[law.id], score: parseInt(e.target.value) }
                      }))}
                      className="w-full accent-brand bg-industrial-900 rounded-lg cursor-pointer"
                    />
                    <textarea 
                      placeholder={`Enter reasoning for ${law.name.toLowerCase()}...`}
                      value={auditScores[law.id].reasoning}
                      onChange={(e) => setAuditScores(prev => ({
                        ...prev,
                        [law.id]: { ...prev[law.id], reasoning: e.target.value }
                      }))}
                      className="w-full bg-industrial-900/50 border border-industrial-800 rounded-lg p-3 text-xs text-slate-300 focus:ring-1 focus:ring-brand outline-none h-20 resize-none"
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center bg-brand/5 p-6 rounded-2xl border border-brand/10">
                <div className="flex gap-4">
                  <div className="p-3 bg-brand/10 rounded-xl text-brand">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xs uppercase font-mono tracking-widest text-industrial-400">Calculated Suitability</div>
                    <div className="text-2xl font-bold">
                      {(Object.values(auditScores).reduce((a, b) => a + b.score, 0) / 4).toFixed(1)} / 10
                    </div>
                  </div>
                </div>
                <button 
                  onClick={handleSaveAudit}
                  disabled={savingAudit}
                  className="btn-primary px-8 py-3 flex items-center gap-2 group"
                >
                  {savingAudit ? <RefreshCw className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5 group-hover:scale-110 transition-transform" />}
                  Finalize & Sync Strategy
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const NavItem = ({ icon: Icon, label, active = false, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${active ? 'bg-brand text-industrial-900 font-bold shadow-lg shadow-brand/20' : 'text-industrial-400 hover:bg-white/5 hover:text-white'}`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-sm">{label}</span>
  </button>
);

const LayoutDashboard = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
);
